import { ColourField, HexField } from './Paint';
import { PropertyField } from './PropertyField';
import { usePress } from '../press';
import { colourFrom, pressHex } from '../ink';
import { colorToHex, rgba } from '../../theme';
import { EFFECT_KINDS, artworkValues, defaultEffect, type LumensManifestEntry, effectName, type Cmyk, type Effect, type ImageNode, type ShaderDef, type SketchControl, type ShaderValue, type ShaderEffect, formatLength, parseLength, type EffectField, type LengthUnit } from '@flobi/artwork-core';
import { EmptyAction, IconButton, Slider, NumberField, Button, PopUpButton, Switch, TextField } from '../../ui/controls';
import { createContext, Fragment, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Menu } from '../../ui/menu';
import { Icon } from '../chrome/icons';
import { Popover, useAnchor } from '../../ui/popover';
import { fetchLibraryItem, libraryEngines, listLibraryShaders, type LibraryShader, type LibraryShelf } from '../../services/lumens';
import { sketchSources } from '../../canvas/sketches';
import { useEffectReorder } from './useEffectReorder';
import { useProTools } from '../pro';
import { tileUrl } from '../content/tileUrl';
import { tip } from '../../ui/tooltip';
import { TILE_NAMES } from './effectLooks';
import type { EffectPreview } from '../useEditorCanvas';

/**
 * Effects: what sits on top of a layer and comes off again — colour, blur, texture and
 * shadow, in Procreate's order.
 *
 * Every control is read off the engine's catalogue rather than written out here, so a
 * field added to an effect appears without this file being touched, and cannot be given
 * a range the schema would refuse. Their order is the order they are applied, so moving
 * one is a real edit. Drag handles and the row menus expose that order.
 */

/**
 * Shadow and Blur first: what a person reaches for. Colour and Texture are the Pro set
 * (audit, 15 Oct: thirteen kinds in one flat list, Shadow last under Glitch), folded under
 * More effects until the Pro tools are on.
 */
const GROUPS: { title: string; group: (typeof EFFECT_KINDS)[number]['group']; pro?: boolean }[] = [
  { title: 'Shadow', group: 'shadow' },
  { title: 'Blur', group: 'blur' },
  { title: 'Colour', group: 'colour', pro: true },
  { title: 'Texture', group: 'texture', pro: true },
];

/** System effects and the user's real Lumens library, alongside the inspector. */
export function AddEffect({ onAdd, onShader, disabled, showLabel = false, preview, previewKey }: { onAdd: (type: Effect['type'], look?: Effect) => void; onShader?: (id: string) => Promise<void>; disabled?: boolean; showLabel?: boolean; preview?: (types: readonly Effect['type'][], longEdge: number, onTile?: (tile: EffectPreview) => void, signal?: AbortSignal) => Promise<EffectPreview[]>; previewKey?: string }) {
  const [anchor, ref] = useAnchor<HTMLButtonElement>();
  const [open, setOpen] = useState(false);
  return <>
    {showLabel ? <EmptyAction ref={ref} icon="plus" disabled={disabled} aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(!open)}>Add effect</EmptyAction>
      : <IconButton ref={ref} icon="plus" label="Add Effect" size={22} disabled={disabled} aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(!open)} />}
    <Popover open={open} anchor={anchor} positionAnchor={anchor?.closest<HTMLElement>('.properties-panel')} onClose={() => setOpen(false)} placement="left" align="end" width={preview ? 340 : 304} label="Add effect" ownsKeyboard className="effect-picker">
      <header className="flex items-center justify-between p-[14px] pb-[10px]"><strong className="text-[13px]">Add effect</strong><IconButton icon="close" label="Close effects" size={24} onClick={() => setOpen(false)} /></header>
      {open ? <EffectChooser onAdd={onAdd} onShader={onShader} disabled={disabled} onAdded={() => setOpen(false)} preview={preview} previewKey={previewKey} /> : null}
    </Popover>
  </>;
}

/** Shared effect catalogue for the inspector and the toolbar's direct editor. */
export function EffectChooser({ onAdd, onShader, disabled, onAdded, preview, previewKey = '' }: {
  onAdd: (type: Effect['type'], look?: Effect) => void; onShader?: (id: string) => Promise<void>; disabled?: boolean; onAdded?: () => void;
  /** Draws the chosen layer with each effect (canvas.previewEffects): given, the catalogue is tiles. */
  preview?: (types: readonly Effect['type'][], longEdge: number, onTile?: (tile: EffectPreview) => void, signal?: AbortSignal) => Promise<EffectPreview[]>;
  /** The layer the tiles are of, as `previewKeyOf` writes it: they are drawn again only when it changes. */
  previewKey?: string;
}) {
  const [view, setView] = useState<'effects' | 'lumens'>('effects');
  const [added, setAdded] = useState('');
  const pro = useProTools();
  const [moreOut, setMoreOut] = useState(false);
  const shown = GROUPS.filter(group => pro || moreOut || !group.pro);
  const folded = !pro && !moreOut;
  const add = (kind: (typeof EFFECT_KINDS)[number], look?: Effect) => { onAdd(kind.type, look); setAdded(kind.name); onAdded?.(); };
  const more = <button type="button" className="effect-picker-row effect-picker-more" onClick={() => setMoreOut(true)}>More effects<Icon name="chevron" size={12} /></button>;
  if (view === 'lumens') return <>
    <div className="effect-chooser-back"><Button variant="borderless" size="small" icon="back" onClick={() => setView('effects')}>Effects</Button></div>
    <LumensLibrary onApply={disabled || !onShader ? undefined : async id => { await onShader(id); onAdded?.(); }} />
  </>;
  const rows = (group: (typeof GROUPS)[number]) => <section key={group.group} className="mb-[8px]"><h3 className="px-[8px] py-[6px] text-[11px] font-medium text-label-3">{group.title}</h3>
    {EFFECT_KINDS.filter(kind => kind.group === group.group).map(kind => <button key={kind.type} disabled={disabled} className="effect-picker-row" onClick={() => add(kind)}>{kind.name}<Icon name="plus" size={14} /></button>)}
  </section>;
  return <>
    {preview ? <EffectTiles kinds={shown.flatMap(group => EFFECT_KINDS.filter(kind => kind.group === group.group))} preview={preview} previewKey={previewKey} disabled={disabled} onPick={add} after={folded ? more : null} />
      : <div className="ui-scroll max-h-[420px] px-[8px] pb-[8px]">{shown.map(rows)}{folded ? more : null}</div>}
    {pro && onShader ? <div className="effect-chooser-foot"><button type="button" className="effect-picker-row" onClick={() => setView('lumens')}>Lumens Library<Icon name="chevron-right" size={12} /></button></div> : null}
    {added && <p role="status" className="effect-added">Added {added}</p>}
  </>;
}

/** Tiles already drawn, by layer and kinds, so opening Effects again on the same layer is instant. */
const TILE_CACHE = new Map<string, Map<string, { look: Effect; url: string | null }>>();
const TILE_CACHE_SIZE = 8;

/** Keep in step with what Artwork can add from Lumens: a shader on a layer, or a sketch as a layer. */
const ADDABLE = new Set(['glsl', 'p5']);

/**
 * Option B of five (Tarek, 16 Oct): every effect drawn on the chosen layer, four across, so it
 * is picked by how it looks. Each tile is the canvas's own drawing of the layer wearing that
 * effect (canvas.previewEffects, from a small copy of its pictures, on the GPU), and a press adds
 * the effect exactly as the tile shows it. Asked for once per change to the layer, never per
 * render: the preview function is a new one every time the bar renders.
 */
function EffectTiles({ kinds, preview, previewKey, disabled, onPick, after }: {
  kinds: readonly (typeof EFFECT_KINDS)[number][]; disabled?: boolean; after: ReactNode; previewKey: string;
  preview: (types: readonly Effect['type'][], longEdge: number, onTile?: (tile: EffectPreview) => void, signal?: AbortSignal) => Promise<EffectPreview[]>;
  onPick: (kind: (typeof EFFECT_KINDS)[number], look?: Effect) => void;
}) {
  const types = kinds.map(kind => kind.type).join(',');
  const key = `${types}|${previewKey}`;
  const [tiles, setTiles] = useState<Map<string, { look: Effect; url: string | null }>>(() => TILE_CACHE.get(key) ?? new Map());
  const draw = useRef(preview);
  draw.current = preview;
  useEffect(() => {
    const known = TILE_CACHE.get(key);
    if (known && known.size === types.split(',').length) { setTiles(known); return; }
    const stop = new AbortController();
    const made = new Map<string, { look: Effect; url: string | null }>();
    setTiles(made);
    // Twice the tile's size, for a sharp picture on a retina screen; each tile shows as it is drawn.
    void draw.current(types.split(',') as Effect['type'][], 144, tile => {
      made.set(tile.type, { look: tile.look, url: tile.png ? URL.createObjectURL(new Blob([tile.png as BlobPart], { type: 'image/png' })) : null });
      if (!stop.signal.aborted) setTiles(new Map(made));
    }, stop.signal).then(() => {
      // A drawing stopped half way is not kept: the next opening draws the whole set.
      if (stop.signal.aborted) { for (const tile of made.values()) if (tile.url) URL.revokeObjectURL(tile.url); return; }
      TILE_CACHE.delete(key);
      TILE_CACHE.set(key, made);
      for (const [old, entry] of TILE_CACHE) {
        if (TILE_CACHE.size <= TILE_CACHE_SIZE) break;
        TILE_CACHE.delete(old);
        for (const tile of entry.values()) if (tile.url) URL.revokeObjectURL(tile.url);
      }
    }).catch(() => {});
    return () => stop.abort();
  }, [key, types]);
  return <div className="ui-scroll effect-tiles-scroll">
    <div className="effect-tiles">
      {kinds.map(kind => {
        const tile = tiles.get(kind.type);
        return <button key={kind.type} type="button" className="effect-tile" disabled={disabled} {...tip(kind.name)} onClick={() => onPick(kind, tile?.look)}>
          <span className="effect-tile-picture" data-loading={!tile || undefined}>{tile?.url ? <img src={tile.url} alt="" draggable={false} /> : null}</span>
          <span className="effect-tile-name">{TILE_NAMES[kind.type] ?? kind.name}</span>
        </button>;
      })}
    </div>
    {after}
  </div>;
}

/**
 * Why a Lumens library item cannot be added. Artwork imports GLSL shaders, which it translates
 * to Skia, and p5 sketches, which run as layers of their own; a composite is a whole stack of
 * Lumens layers (text, pictures, effects), which is neither.
 */
export function unavailableReason(engine: string | undefined): { short: string; detail: string } {
  if (engine === 'composite' || !engine) return { short: 'Layer stack · not a single shader', detail: 'This is a stack of Lumens layers, not one shader. Artwork can only add a single GLSL shader as an effect.' };
  if (engine === 'builtin') return { short: 'Lumens built-in · not available here', detail: 'This uses one of Lumens’ built-in effects, which Artwork does not have.' };
  return { short: 'Unavailable in Artwork', detail: 'This library item is not a GLSL shader.' };
}

/** The Lumens Library's own shelves, named as its Library page names them. */
const SHELVES: readonly (readonly [LibraryShelf, string])[] = [['owned', 'Yours'], ['shared', 'Shared with you'], ['workspace', 'Workspace']];

function LumensLibrary({ onApply }: { onApply?: (id: string) => Promise<void> }) {
  const [tier, setTier] = useState<LibraryShelf>('owned');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<LibraryShader[]>([]);
  const [cursor, setCursor] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [applying, setApplying] = useState<string>();
  const [applyError, setApplyError] = useState<{ id: string; message: string } | null>(null);
  const [added, setAdded] = useState<string>();
  const [retry, setRetry] = useState(0);
  const request = useRef(0);
  const setEngine = (id: string, engine: string) => setItems(old => old.map(item => item.id === id ? { ...item, signature: { engine } } : item));
  // So the first sketch placed has nothing left to download.
  useEffect(() => { void sketchSources(); }, []);
  useEffect(() => {
    const token = ++request.current;
    setItems([]); setCursor(undefined); setLoading(true); setError('');
    const timer = setTimeout(() => {
      listLibraryShaders(tier, query).then(result => {
        if (token !== request.current) return;
        setItems(result.items ?? []); setCursor(result.hasMore ? result.nextCursor : undefined);
        libraryEngines(result.items ?? [], setEngine, () => token === request.current);
      }).catch(() => { if (token === request.current) setError('Could not load Lumens. Try again.'); })
        .finally(() => { if (token === request.current) setLoading(false); });
    }, 200);
    return () => { clearTimeout(timer); request.current++; };
  }, [tier, query, retry]);
  const more = async () => {
    const token = request.current;
    setLoading(true);
    try {
      const result = await listLibraryShaders(tier, query, cursor);
      if (token !== request.current) return;
      setItems(old => [...old, ...result.items.filter(item => !old.some(one => one.id === item.id))]);
      setCursor(result.hasMore ? result.nextCursor : undefined);
      libraryEngines(result.items, setEngine, () => token === request.current);
    } catch { if (token === request.current) setError('Could not load more shaders. Try again.'); }
    finally { if (token === request.current) setLoading(false); }
  };
  return <div className="px-[14px] pb-[14px]">
    <div className="mb-[10px] flex gap-[6px]">{SHELVES.map(([value, label]) => <button key={value} aria-pressed={tier === value} className={`rounded-[6px] px-[10px] py-[6px] text-[11px] ${tier === value ? 'bg-fill-1 text-label' : 'text-label-2'}`} onClick={() => setTier(value)}>{label}</button>)}</div>
    <input aria-label="Search your Lumens Library" placeholder="Search Library" maxLength={100} value={query} onChange={event => setQuery(event.target.value)} className="mb-[10px] h-[32px] w-full rounded-[7px] bg-fill-3 px-[10px] text-[12px]" />

    {error && <div role="alert" className="mb-[10px] text-[12px] text-label-2">{error} <button className="underline" onClick={() => setRetry(value => value + 1)}>Retry</button></div>}
    <div className="ui-scroll max-h-[320px] space-y-[4px]">
      {items.map(item => {
        const unsupported = !!item.signature?.engine && !ADDABLE.has(item.signature.engine);
        const why = unsupported ? unavailableReason(item.signature?.engine) : null;
        const sketch = item.signature?.engine === 'p5';
        return <div key={item.id}><button disabled={unsupported || !!applying || !onApply} className="effect-picker-row shader-picker-row disabled:opacity-45" title={why ? why.detail : item.name} onPointerEnter={() => { if (!unsupported) fetchLibraryItem(item.id).catch(() => undefined); }} onClick={async () => {
          setApplying(item.id); setApplyError(null); setAdded(undefined);
          try { await onApply?.(item.id); setAdded(item.id); }
          catch (error) { setApplyError({ id: item.id, message: error instanceof Error ? error.message : 'Could not add this shader.' }); }
          finally { setApplying(undefined); }
        }}>
          <span className="h-[38px] w-[48px] shrink-0 overflow-hidden rounded-[5px] bg-fill-3">{item.coverUrl ? <img src={tileUrl(item.coverUrl)} alt="" className="h-full w-full object-cover" onError={event => { event.currentTarget.style.display = 'none'; }} /> : null}</span>
          <span className="min-w-0 flex-1 text-left"><span className="block truncate">{item.name}</span>{(applying === item.id || added === item.id || unsupported || sketch) && <span className="block text-[10px] text-label-3">{applying === item.id ? 'Adding…' : added === item.id ? 'Added' : why?.short ?? 'Animated layer'}</span>}</span>
          {!unsupported && <Icon name="plus" size={14} />}
        </button>{applyError?.id === item.id && <p role="alert" className="shader-error">{applyError.message}</p>}</div>;
      })}
      {loading && <p role="status" className="py-[14px] text-[12px] text-label-2">Loading Library…</p>}
      {!loading && !error && !items.length && <p className="py-[14px] text-[12px] text-label-2">{query ? 'No matching effects.' : 'No saved effects here yet.'}</p>}
      {cursor && !loading && <Button size="small" onClick={more}>Load more</Button>}
    </div>
  </div>;
}

export function EffectsList({
  effects,
  units,
  onChange,
  onRemove,
  onReorder,
  getShader,
  collapsible = true,
  initialExpanded,
  onOpen,
}: {
  effects: readonly Effect[];
  collapsible?: boolean;
  /**
   * The properties panel's way (Tarek, 2 Nov: "drill into the panel"): each effect is one
   * row — settings, name, eye, more — and choosing it opens its own page in the panel
   * (EffectPage) instead of unfolding here.
   */
  onOpen?: (index: number) => void;
  /** A quick editor can reveal the last applied effect when returning from its picker. */
  initialExpanded?: number;
  getShader?: (id: string) => ShaderDef | null;
  units: LengthUnit;
  onChange: (index: number, props: Record<string, unknown>) => void;
  onRemove: (index: number) => void;
  onReorder: (index: number, to: number) => void;
}) {
  const controlsId = useId();
  const [announcement, setAnnouncement] = useState('');
  const stack = useRef<HTMLDivElement>(null);
  const mounted = useRef(false);
  const [expanded, setExpanded] = useState<number | null>(() => initialExpanded ?? (!onOpen && effects.length === 1 ? 0 : null));
  const previousCount = useRef(effects.length);
  useEffect(() => {
    // Open the newly appended effect. A remote removal must not open an unrelated row.
    if (effects.length > previousCount.current) setExpanded(effects.length - 1);
    else if (effects.length < previousCount.current) setExpanded(null);
    previousCount.current = effects.length;
  }, [effects.length]);
  useEffect(() => {
    // Do not scroll the inspector on initial selection. Keep an explicitly opened or
    // newly added row reachable when the surrounding popover hits its height limit.
    if (mounted.current && expanded !== null) {
      stack.current?.children[expanded]?.querySelector('.effect-entry-header')?.scrollIntoView({ block: 'nearest' });
    }
    mounted.current = true;
  }, [expanded]);
  const move = (from: number, to: number) => {
    if (from === to || to < 0 || to >= effects.length) return;
    setExpanded(current => current === null ? null : current === from ? to
      : from < current && to >= current ? current - 1
      : from > current && to <= current ? current + 1 : current);
    onReorder(from, to);
    setAnnouncement(`Effect moved to position ${to + 1} of ${effects.length}.`);
    requestAnimationFrame(() => stack.current?.children[to]?.querySelector<HTMLButtonElement>('.effect-drag-handle')?.focus({ preventScroll: true }));
  };
  const reorder = useEffectReorder(stack, JSON.stringify(effects), move);
  if (effects.length === 0) return null;
  const draggedEffect = reorder.drag ? effects[reorder.drag.from] : undefined;
  const draggedName = draggedEffect?.type === 'shader' ? getShader?.(draggedEffect.shaderId)?.name ?? 'Shader' : draggedEffect ? effectName(draggedEffect) : '';
  return (
    <><div ref={stack} className="effect-stack" data-rows={onOpen ? '' : undefined} data-dragging={reorder.drag !== null || undefined}>
      {effects.map((effect, index) => {
        const on = effect.visible !== false;
        const shader = effect.type === 'shader' ? getShader?.(effect.shaderId) : null;
        const name = shader?.name ?? (effect.type === 'shader' ? 'Shader' : effectName(effect));
        const detailsOpen = !collapsible || expanded === index;
        return (
          <div key={`${effect.type}-${index}`} className="effect-entry" data-expanded={detailsOpen} data-disabled={!on}
            data-drag-source={reorder.drag?.from === index || undefined}
            data-drop-valid={(reorder.drag && reorder.drag.to !== null) || undefined}
            style={reorder.drag ? { transform: `translateY(${reorder.drag.offsets[index]}px)` } : undefined}>
            <div className="effect-entry-header">
              {effects.length > 1 && <button type="button" className="effect-drag-handle" aria-label={`Reorder ${name}`} aria-describedby={`${controlsId}-reorder-help`} title="Drag to reorder" {...reorder.handlers(index)}
                onKeyDown={event => {
                  const to = event.key === 'ArrowUp' ? index - 1 : event.key === 'ArrowDown' ? index + 1 : event.key === 'Home' ? 0 : event.key === 'End' ? effects.length - 1 : null;
                  if (to !== null) { event.preventDefault(); event.stopPropagation(); move(index, to); }
                }}><Icon name="grip-vertical" size={14} /></button>}
              {onOpen ? <>
                <IconButton icon="adjust" label={`${name} settings`} size={22} onClick={() => onOpen(index)} />
                <button type="button" className="effect-row-name" onClick={() => onOpen(index)}>
                  <span className="truncate">{name}</span>{!on && <span className="effect-off">Off</span>}
                </button>
              </> : collapsible ? <button type="button" aria-label={`${detailsOpen ? 'Collapse' : 'Expand'} ${name} settings`} aria-expanded={detailsOpen} aria-controls={`${controlsId}-${index}`} title={name}
                className="effect-disclosure"
                onClick={() => setExpanded(current => current === index ? null : index)}>
                <span className={`shrink-0 transition-transform ${detailsOpen ? 'rotate-90' : ''}`}><Icon name="chevron-right" size={14} /></span><span className="truncate">{name}</span>{!on && <span className="effect-off">Off</span>}
              </button> : <span className={`min-w-0 flex-1 truncate pl-[2px] text-[12px] font-medium ${on ? 'text-label' : 'text-label-3'}`}>{name}</span>}
              <IconButton icon={on ? 'eye' : 'eye-off'} label={`${on ? 'Hide' : 'Show'} ${name}`} size={22} onClick={() => onChange(index, { visible: !on })} />
              <EffectActions name={name} first={index === 0} last={index === effects.length - 1} onUp={() => move(index, index - 1)} onDown={() => move(index, index + 1)} onRemove={() => { setExpanded(null); onRemove(index); }} />
            </div>
              {!onOpen && <fieldset id={`${controlsId}-${index}`} hidden={!detailsOpen} disabled={!on} aria-label={`${name} settings`} className={`effect-controls inspector-grid m-0 min-w-0 border-0 ${on ? '' : 'pointer-events-none opacity-50'} ${detailsOpen ? '' : '!hidden'}`}>
                <EffectControls effect={effect} shader={shader} units={units} onChange={props => onChange(index, props)} />
              </fieldset>}
          </div>
        );
      })}
    </div>
    {reorder.drag && createPortal(<div className="effect-drag-preview" aria-hidden="true" data-valid={reorder.drag.to !== null}
      style={{ width: reorder.drag.width, transform: `translate3d(${reorder.drag.left}px, ${reorder.drag.top}px, 0)` }}>
      <Icon name="grip-vertical" size={14} /><span className="effect-drag-name">{draggedName}</span>
      <span className="effect-drag-position">{reorder.drag.to === null ? 'Cancel' : `${reorder.drag.to + 1} / ${effects.length}`}</span>
    </div>, document.body)}
    <span id={`${controlsId}-reorder-help`} className="sr-only">Drag to reorder, or use the Up and Down arrow keys. Effects are applied from top to bottom.</span><span className="sr-only" role="status">{announcement}</span></>
  );
}

/** An effect's own settings, wherever they are shown: unfolded in a list, or on its page. */
function EffectControls({ effect, shader, units, onChange }: { effect: Effect; shader: ShaderDef | null | undefined; units: LengthUnit; onChange: (props: Record<string, unknown>) => void }) {
  const kind = EFFECT_KINDS.find((one) => one.type === effect.type);
  return <>
                {(effect.type === 'inner-shadow' || effect.type === 'drop-shadow') && <>
                  <EffectColour label="Colour" pickerLabel="Shadow colour" hex={colorToHex(effect.color)} ink={effect.color.cmyk} alpha={effect.color.a} onChange={(hex, ink) => onChange({ color: colourFrom(hex, ink, effect.color.a ?? 1) })} />
                  <PropertyField label="Colour opacity"><NumberField label="Shadow opacity" value={effect.color.a * 100} min={0} max={100} suffix="%" onChange={value => onChange({ color: { ...effect.color, a: value / 100 } })} /></PropertyField>
                </>}
                {effect.type === 'background-blur' && <p className="inspector-span text-[10px] leading-relaxed text-label-3">Use a translucent fill to reveal the background blur.</p>}
                {effect.type === 'hsb' && <p className="inspector-span text-[10px] leading-relaxed text-label-3">Hue and saturation affect coloured fills; black and white have no hue.</p>}
                {effect.type === 'sharpen' && <p className="inspector-span text-[10px] leading-relaxed text-label-3">Works best on soft images.</p>}
                {effect.type === 'shader' && !shader && <p className="inspector-span text-[11px] text-label-2">This shader’s settings are unavailable.</p>}
                {effect.type === 'shader' && <ShaderFields effect={effect} definition={shader} onChange={onChange} />}
                {(kind?.fields ?? []).map((field) => (
                  <EffectSlider
                    key={field.key}
                    field={field}
                    units={units}
                    value={Number((effect as unknown as Record<string, unknown>)[field.key] ?? 0)}
                    onChange={(value) => onChange({ [field.key]: value })}
                  />
                ))}
  </>;
}

/**
 * A sketch layer's settings: what its code offers, each kept as the raw value `p.lumen.params`
 * hands it, then how long it plays. A seed control is the one button: a new variation.
 */
export function SketchSettings({ node, controls, error, units, sources = [], pictures = [], chosen = {}, onPick, thumbnailOf, onChange }: {
  node: ImageNode;
  controls: readonly SketchControl[];
  error: string | null;
  units: LengthUnit;
  /** The layers a control that reads a layer (Letter Boxes' "Text") can name, and which each names now. */
  sources?: readonly { id: string; name: string }[];
  /** The pictures and artboards a picture control (Photo Cube's faces) can show. */
  pictures?: readonly { id: string; name: string }[];
  chosen?: Readonly<Record<string, string | null>>;
  /** Fills a picture slot from the canvas (`CanvasHandle.pickLayer`). */
  onPick?: (candidates: readonly string[], then: (id: string | null) => void) => void;
  thumbnailOf?: (id: string) => Promise<string | null>;
  onChange: (props: Record<string, unknown>) => void;
}) {
  const [picking, setPicking] = useState<string | null>(null);
  const sketch = node.sketch!;
  const set = (id: string, value: unknown) => onChange({ sketch: { ...sketch, values: { ...sketch.values, [id]: value } } });
  const shown = controls.filter(control => control.type !== 'seed');
  const field = (control: SketchControl) => {
    const label = control.label || control.id;
    const value = sketch.values[control.id] ?? control.default;
    // Words a sketch draws (Lumens' text and textarea controls), and a font family it sets them in.
    if (control.type === 'text' || control.type === 'font') return <PropertyField key={control.id} label={label}><TextField label={label} value={String(value ?? '')} onChange={next => set(control.id, next)} /></PropertyField>;
    if (control.type === 'textarea') return <PropertyField key={control.id} label={label}><textarea aria-label={label} rows={3} value={String(value ?? '')} onChange={event => set(control.id, event.target.value)} onKeyDown={event => event.stopPropagation()} className="field-focus w-full resize-y rounded-[9px] bg-fill-1 px-[8px] py-[6px] text-[13px] text-label outline-none" /></PropertyField>;
    // A point, as Lumens stores it ({ x, y }).
    if (control.type === 'vector2' && value && typeof value === 'object') {
      const point = value as { x?: number; y?: number };
      return <PropertyField key={control.id} label={label}><div className="flex gap-[6px]">
        <NumberField label={`${label} X`} value={Number(point.x ?? 0)} step={control.step ?? .01} min={control.min} max={control.max} onChange={x => set(control.id, { ...point, x })} />
        <NumberField label={`${label} Y`} value={Number(point.y ?? 0)} step={control.step ?? .01} min={control.min} max={control.max} onChange={y => set(control.id, { ...point, y })} />
      </div></PropertyField>;
    }
    // A palette is its colours, each set as any colour is ("Played bars 1", "Played bars 2").
    if (control.type === 'palette' && Array.isArray(value)) return <Fragment key={control.id}>{(value as unknown[]).map((hex, index) => <EffectColour key={`${control.id}-${index}`} label={`${label} ${index + 1}`} hex={String(hex ?? '#000000').slice(0, 7)} alpha={1} onChange={next => set(control.id, (value as unknown[]).map((old, at) => at === index ? next : old))} />)}</Fragment>;
    if (control.type === 'media') {
      const id = chosen[control.id] ?? null;
      const name = id ? pictures.find(picture => picture.id === id)?.name ?? 'Picture' : null;
      return <PropertyField key={control.id} label={label}><PictureSlot label={label} id={id} name={name} picking={picking === control.id} thumbnailOf={thumbnailOf}
        onPick={onPick && pictures.length ? () => {
          if (picking === control.id) return;
          setPicking(control.id);
          onPick(pictures.map(picture => picture.id), picked => { setPicking(null); if (picked) set(control.id, { layer: picked }); });
        } : undefined}
        onClear={() => set(control.id, null)} /></PropertyField>;
    }
    if (control.type === 'shape') return <PropertyField key={control.id} label={label}><PopUpButton label={label} value={chosen[control.id] ?? ''} options={[...(chosen[control.id] ? [] : [['', 'Choose a layer'] as const]), ...sources.map(source => [source.id, source.name] as const)]} onChange={next => { if (next) set(control.id, { layer: next }); }} /></PropertyField>;
        if (control.type === 'color') return <EffectColour key={control.id} label={label} hex={String(value ?? '#000000').slice(0, 7)} alpha={1} onChange={hex => set(control.id, hex)} />;
    if (control.type === 'toggle' || control.type === 'boolean') return <PropertyField key={control.id} label={label}><div className="effect-switch"><Switch label={label} checked={Boolean(value)} onChange={next => set(control.id, next)} /></div></PropertyField>;
    if (control.options?.length) return <PropertyField key={control.id} label={label}><PopUpButton label={label} value={String(value)} options={control.options.map(option => [String(option.value), option.label ?? String(option.value)] as const)} onChange={next => set(control.id, control.options!.find(option => String(option.value) === next)?.value ?? next)} /></PropertyField>;
    if (typeof value !== 'number') return null;
    const whole = control.type === 'stepper';
    return <EffectSlider key={control.id} field={{ key: control.id, label, min: control.min ?? 0, max: control.max ?? (whole ? 100 : 1), step: control.step ?? (whole ? 1 : .01), unit: 'plain' }} units={units} value={value} onChange={next => set(control.id, next)} />;
  };
  return <>
    {error ? <p role="alert" className="shader-error">{error}</p> : null}
    {shown.map(field)}
    <PropertyField label="Variation"><Button size="small" icon="effector-random" onClick={() => onChange({ sketch: { ...sketch, seed: 1 + Math.floor(Math.random() * 1_000_000) } })}>Shuffle</Button></PropertyField>
    <PropertyField label="Plays for"><NumberField label="Plays for" value={node.clip?.duration ?? 6} min={1} max={60} step={1} suffix="s" onChange={duration => onChange({ clip: { duration } })} /></PropertyField>
  </>;
}

/**
 * A sketch's picture slot (Photo Cube's faces): a thumbnail of what fills it and its name, Pick to
 * choose a picture or an artboard on the canvas (they are outlined until one is pressed, Esc to
 * stop), and a clear button. Tarek, 3 Nov: a list of names gave no way to tell which was which.
 */
function PictureSlot({ label, id, name, picking, thumbnailOf, onPick, onClear }: {
  label: string; id: string | null; name: string | null; picking: boolean;
  thumbnailOf?: (id: string) => Promise<string | null>; onPick?: () => void; onClear: () => void;
}) {
  const [thumb, setThumb] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setThumb(null);
    if (id && thumbnailOf) void thumbnailOf(id).then(url => { if (live) setThumb(url); });
    return () => { live = false; };
  }, [id, thumbnailOf]);
  return <div className="flex min-w-0 items-center gap-[6px]">
    <span className="flex size-[28px] shrink-0 items-center justify-center overflow-hidden rounded-[6px] bg-fill-1">{thumb ? <img src={thumb} alt="" className="max-h-full max-w-full object-contain" /> : null}</span>
    <span className={`min-w-0 flex-1 truncate text-[12px] ${name ? 'text-label' : 'text-label-3'}`}>{picking ? 'Click a picture or artboard…' : name ?? 'Empty'}</span>
    {onPick ? <Button size="small" icon="cursor" onClick={onPick} aria-pressed={picking}>{picking ? 'Picking' : 'Pick'}</Button> : null}
    {id ? <IconButton icon="close" label={`Clear ${label}`} size={22} onClick={onClear} /> : null}
  </div>;
}

/** How an effect's numbers are drawn: a field over a slider, or one bar that is both (EffectPage). */
const BarFields = createContext(false);

/**
 * One effect, given the whole panel (Tarek, 2 Nov: "drill into the panel — but presets should
 * be a dropdown"). Back returns to the properties. Every number is a bar: drag it, or click
 * to type. Presets are a menu: the effect's defaults, and a subtler and a stronger take on
 * what it has now.
 */
/**
 * A Lumens sketch in the layer's Effects list: one row like any effect's (settings, name, eye,
 * more), opening its own page (`SketchPage`). The sketch is what draws the layer, so its eye
 * shows and hides the layer and Remove takes the layer away.
 */
export function SketchRow({ name, on, onOpen, onToggle, onRemove }: { name: string; on: boolean; onOpen: () => void; onToggle: () => void; onRemove: () => void }) {
  return <div className="effect-stack" data-rows="">
    <div className="effect-entry" data-expanded={false} data-disabled={!on}>
      <div className="effect-entry-header">
        <IconButton icon="adjust" label={`${name} settings`} size={22} onClick={onOpen} />
        <button type="button" className="effect-row-name" onClick={onOpen}>
          <span className="truncate">{name}</span>{!on && <span className="effect-off">Off</span>}
        </button>
        <IconButton icon={on ? 'eye' : 'eye-off'} label={`${on ? 'Hide' : 'Show'} ${name}`} size={22} onClick={onToggle} />
        <EffectActions name={name} first last onUp={() => {}} onDown={() => {}} onRemove={onRemove} />
      </div>
    </div>
  </div>;
}

/**
 * A Lumens sketch's own page, as an effect's is (`EffectPage`): back, eye, more, the looks its
 * author saved in Lumens as Presets, and every control as the same bars. A control that names a
 * layer keeps the layer it names here: a preset's names one in the Lumens frame it came from.
 */
export function SketchPage({ node, name, controls, error, units, sources, pictures, chosen, presets = [], onPick, thumbnailOf, onChange, onBack, onRemove }: {
  node: ImageNode;
  name: string;
  controls: readonly SketchControl[];
  error: string | null;
  units: LengthUnit;
  sources?: readonly { id: string; name: string }[];
  pictures?: readonly { id: string; name: string }[];
  chosen?: Readonly<Record<string, string | null>>;
  onPick?: (candidates: readonly string[], then: (id: string | null) => void) => void;
  thumbnailOf?: (id: string) => Promise<string | null>;
  presets?: readonly { name: string; seed?: number; values: Record<string, unknown> }[];
  onChange: (props: Record<string, unknown>) => void;
  onBack: () => void;
  onRemove: () => void;
}) {
  const on = node.visible !== false;
  const sketch = node.sketch!;
  const back = useRef<HTMLButtonElement>(null);
  useEffect(() => { back.current?.focus({ preventScroll: true }); }, []);
  const usePreset = (index: number) => {
    const look = presets[index];
    if (!look) return;
    const values = Object.fromEntries(Object.entries(look.values).filter(([id]) => id !== 'seed' && !['shape', 'media'].includes(controls.find(c => c.id === id)?.type ?? '')));
    const seed = typeof look.values.seed === 'number' ? Math.abs(Math.round(look.values.seed)) % 2 ** 31 : sketch.seed;
    onChange({ sketch: { ...sketch, seed, values: { ...sketch.values, ...values } } });
  };
  // The look the sketch is set to now, so the menu shows it (Tarek, 3 Nov: it stayed on "Presets…").
  const appliedLook = presets.findIndex(look => {
    const seed = typeof look.values.seed === 'number' ? Math.abs(Math.round(look.values.seed)) % 2 ** 31 : undefined;
    if (seed !== undefined && seed !== sketch.seed) return false;
    return Object.entries(look.values).every(([id, value]) => id === 'seed' || ['shape', 'media'].includes(controls.find(c => c.id === id)?.type ?? '') || JSON.stringify(sketch.values[id] ?? controls.find(c => c.id === id)?.default) === JSON.stringify(value));
  });
  return <div className="effect-page" role="region" aria-label={`${name} settings`} onKeyDown={event => { if (event.key === 'Escape' && !event.defaultPrevented) { event.preventDefault(); onBack(); } }}>
    <div className="effect-page-header">
      <IconButton ref={back} icon="back" label="Back to properties" onClick={onBack} />
      <h3 className="min-w-0 flex-1 truncate">{name}</h3>
      <IconButton icon={on ? 'eye' : 'eye-off'} label={`${on ? 'Hide' : 'Show'} ${name}`} onClick={() => onChange({ visible: !on })} />
      <EffectActions name={name} first last onUp={() => {}} onDown={() => {}} onRemove={() => { onRemove(); onBack(); }} />
    </div>
    {presets.length > 0 && <div className="effect-page-presets">
      <PopUpButton label="Preset" value={appliedLook >= 0 ? String(appliedLook) : ''} options={[['', appliedLook >= 0 ? 'Presets…' : 'Custom'] as const, ...presets.map((look, index) => [String(index), look.name] as const)]} onChange={next => { if (next !== '') usePreset(Number(next)); }} />
    </div>}
    <fieldset disabled={!on} aria-label={`${name} settings`} className={`effect-page-controls m-0 min-w-0 border-0 ${on ? '' : 'opacity-50'}`}>
      <BarFields.Provider value={true}>
        <SketchSettings node={node} controls={controls} error={error} units={units} sources={sources} pictures={pictures} chosen={chosen} onPick={onPick} thumbnailOf={thumbnailOf} onChange={onChange} />
      </BarFields.Provider>
    </fieldset>
  </div>;
}

export function EffectPage({ effect, shader, units, onChange, onBack, onRemove }: {
  effect: Effect;
  shader?: ShaderDef | null;
  units: LengthUnit;
  onChange: (props: Record<string, unknown>) => void;
  onBack: () => void;
  onRemove: () => void;
}) {
  const on = effect.visible !== false;
  const name = shader?.name ?? (effect.type === 'shader' ? 'Shader' : effectName(effect));
  const back = useRef<HTMLButtonElement>(null);
  useEffect(() => { back.current?.focus({ preventScroll: true }); }, []);
  const presets = effectPresets(effect, shader);
  // The preset the effect is set to now, if any: every value it sets is the effect's.
  const applied = presets.find(preset => Object.entries(preset.props).every(([key, value]) => {
    const now = (effect as unknown as Record<string, unknown>)[key];
    if (key === 'values' && value && typeof value === 'object') return Object.entries(value).every(([id, v]) => JSON.stringify((now as Record<string, unknown> | undefined)?.[id]) === JSON.stringify(v));
    return JSON.stringify(now) === JSON.stringify(value);
  }));
  return <div className="effect-page" role="region" aria-label={`${name} settings`} onKeyDown={event => { if (event.key === 'Escape' && !event.defaultPrevented) { event.preventDefault(); onBack(); } }}>
    <div className="effect-page-header">
      <IconButton ref={back} icon="back" label="Back to properties" onClick={onBack} />
      <h3 className="min-w-0 flex-1 truncate">{name}</h3>
      <IconButton icon={on ? 'eye' : 'eye-off'} label={`${on ? 'Hide' : 'Show'} ${name}`} onClick={() => onChange({ visible: !on })} />
      <EffectActions name={name} first last onUp={() => {}} onDown={() => {}} onRemove={() => { onRemove(); onBack(); }} />
    </div>
    {presets.length > 0 && <div className="effect-page-presets">
      <PopUpButton label="Preset" value={applied?.id ?? ''} options={[['', applied ? 'Presets…' : 'Custom'] as const, ...presets.map(preset => [preset.id, preset.label] as const)]} onChange={id => { const preset = presets.find(one => one.id === id); if (preset) onChange(preset.props); }} />
    </div>}
    <fieldset disabled={!on} aria-label={`${name} settings`} className={`effect-page-controls m-0 min-w-0 border-0 ${on ? '' : 'opacity-50'}`}>
      <BarFields.Provider value={true}>
        <EffectControls effect={effect} shader={shader} units={units} onChange={onChange} />
      </BarFields.Provider>
    </fieldset>
  </div>;
}

/**
 * What the Presets menu offers: the looks a Lumens effect's author saved, then what every effect
 * can have — its defaults, and the numbers it has now taken halfway back toward their low end, or
 * half again further.
 */
function effectPresets(effect: Effect, shader?: ShaderDef | null): { id: string; label: string; props: Record<string, unknown> }[] {
  type Range = { key: string; min: number; max: number; value: number; fallback: number };
  const scale = (ranges: Range[], by: number) => Object.fromEntries(ranges.map(r => [r.key, Math.min(r.max, Math.max(r.min, r.min + (r.value - r.min) * by))]));
  if (effect.type === 'shader') {
    if (!shader) return [];
    // The looks the effect's author saved in Lumens come first, as Lumens lists them.
    const manifest = (shader.gl?.manifest ?? []) as unknown as LumensManifestEntry[];
    const saved = (shader.gl?.presets ?? []).map((preset, index) => {
      const seed = typeof preset.values.seed === 'number' ? Math.round(preset.values.seed) : undefined;
      return { id: `lumens-${index}`, label: preset.name, props: { values: { ...effect.values, ...artworkValues(manifest, preset.values) }, ...(seed !== undefined ? { seed: Math.abs(seed) % 2 ** 31 } : {}) } };
    });
    // A Lumens effect offers only what its author saved there (Tarek, 3 Nov: "I don't have such presets").
    if (shader.gl) return saved;
    const numbers: Range[] = shader.controls.filter(c => c.type === 'number' && !/angle|seed|rotation|direction/i.test(c.id + c.label)).map(c => ({ key: c.id, min: c.min ?? 0, max: c.max ?? 100, value: Number(effect.values[c.id] ?? c.default ?? 0), fallback: Number(c.default ?? 0) }));
    const defaults = Object.fromEntries(shader.controls.filter(c => c.default !== undefined).map(c => [c.id, c.default!]));
    return [
      ...saved,
      { id: 'default', label: 'Default', props: { values: { ...effect.values, ...defaults } } },
      ...(numbers.length ? [
        { id: 'subtle', label: 'Subtle', props: { values: { ...effect.values, ...scale(numbers, 0.5) } } },
        { id: 'strong', label: 'Strong', props: { values: { ...effect.values, ...scale(numbers, 1.5) } } },
      ] : []),
    ];
  }
  const kind = EFFECT_KINDS.find((one) => one.type === effect.type);
  const fields = (kind?.fields ?? []).filter(f => f.unit !== 'degrees');
  const base = defaultEffect(effect.type) as Record<string, unknown> | null;
  const ranges: Range[] = fields.map(f => ({ key: f.key, min: f.min, max: f.max, value: Number((effect as unknown as Record<string, unknown>)[f.key] ?? 0), fallback: Number(base?.[f.key] ?? 0) }));
  const defaults = base ? Object.fromEntries((kind?.fields ?? []).map(f => [f.key, base[f.key]])) : {};
  return [
    ...(base ? [{ id: 'default', label: 'Default', props: defaults }] : []),
    ...(ranges.length ? [{ id: 'subtle', label: 'Subtle', props: scale(ranges, 0.5) }, { id: 'strong', label: 'Strong', props: scale(ranges, 1.5) }] : []),
  ];
}

/**
 * A number that is its own slider: the field fills to where the value sits in its range.
 * Drag across it to change it, click it to type, arrows nudge (Shift for ten).
 */
function BarField({ label, value, min, max, step, format, parse, suffix, onChange }: {
  label: string; value: number; min: number; max: number; step: number;
  format: (value: number) => string; parse?: (text: string) => number | null; suffix?: string;
  onChange: (value: number) => void;
}) {
  const [typing, setTyping] = useState<string | null>(null);
  const drag = useRef<{ x: number; from: number; width: number; moved: boolean } | null>(null);
  const clamp = (n: number) => Math.min(max, Math.max(min, Math.round(n / step) * step));
  const share = max > min ? Math.min(1, Math.max(0, (value - min) / (max - min))) : 0;
  const commit = () => {
    if (typing === null) return;
    const read = parse ? parse(typing) : Number(typing.replace(/[^0-9.+-]/g, ''));
    if (read !== null && Number.isFinite(read)) onChange(clamp(read));
    setTyping(null);
  };
  if (typing !== null) return <input className="bar-field bar-field-input" aria-label={label} autoFocus value={typing}
    onChange={event => setTyping(event.target.value)} onBlur={commit}
    onKeyDown={event => { if (event.key === 'Enter') commit(); if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setTyping(null); } }} />;
  return <div className="bar-field" role="slider" tabIndex={0} aria-label={label} aria-valuemin={min} aria-valuemax={max} aria-valuenow={value} aria-valuetext={`${format(value)}${suffix ? ` ${suffix}` : ''}`}
    onPointerDown={event => { if (event.button !== 0) return; event.currentTarget.setPointerCapture(event.pointerId); drag.current = { x: event.clientX, from: value, width: event.currentTarget.getBoundingClientRect().width, moved: false }; }}
    onPointerMove={event => {
      const d = drag.current; if (!d) return;
      if (!d.moved && Math.abs(event.clientX - d.x) < 3) return;
      d.moved = true;
      onChange(clamp(d.from + (event.clientX - d.x) / d.width * (max - min) * (event.shiftKey ? 0.1 : 1)));
    }}
    onPointerUp={() => { const d = drag.current; drag.current = null; if (d && !d.moved) setTyping(format(value)); }}
    onPointerCancel={() => { drag.current = null; }}
    onKeyDown={event => {
      const by = event.key === 'ArrowRight' || event.key === 'ArrowUp' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowDown' ? -1 : 0;
      if (by) { event.preventDefault(); onChange(clamp(value + by * step * (event.shiftKey ? 10 : 1))); }
      else if (event.key === 'Enter') { event.preventDefault(); setTyping(format(value)); }
    }}>
    <i className="bar-field-fill" style={{ width: `${share * 100}%` }} />
    <span className="bar-field-label">{label}</span>
    <span className="bar-field-value">{format(value)}{suffix ? <em>{suffix}</em> : null}</span>
  </div>;
}

function EffectActions({ name, first, last, onUp, onDown, onRemove }: { name: string; first: boolean; last: boolean; onUp: () => void; onDown: () => void; onRemove: () => void }) {
  const [anchor, ref] = useAnchor<HTMLButtonElement>();
  const [open, setOpen] = useState(false);
  return <>
    <IconButton ref={ref} icon="more" label={`${name} actions`} size={22} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)} />
    <Menu open={open} anchor={anchor} onClose={() => setOpen(false)} placement="left" label="Effect actions" items={[
      { id: 'up', label: 'Move up', disabled: first, onSelect: onUp },
      { id: 'down', label: 'Move down', disabled: last, onSelect: onDown },
      { kind: 'separator' },
      { id: 'remove', label: 'Remove effect', onSelect: onRemove },
    ]} />
  </>;
}

function EffectSlider({ field, value, units, onChange }: { field: EffectField; value: number; units: LengthUnit; onChange: (value: number) => void }) {
  const multiplier = field.unit === 'percent' ? 100 : 1;
  const suffix = field.unit === 'percent' ? '%' : field.unit === 'degrees' ? '°' : field.unit === 'length' ? (units === 'ft-in' ? undefined : units) : undefined;
  const bars = useContext(BarFields);
  if (bars) {
    const step = field.step * multiplier;
    const decimals = step >= 1 ? 0 : step >= 0.1 ? 1 : 2;
    return <BarField label={field.label} value={value * multiplier} min={field.min * multiplier} max={field.max * multiplier} step={step} suffix={suffix}
      format={field.unit === 'length' ? (n: number) => formatLength(n, units, { bare: true }) : (n: number) => String(Number(n.toFixed(decimals)))}
      parse={field.unit === 'length' ? (text: string) => parseLength(text, units) : undefined}
      onChange={next => onChange(next / multiplier)} />;
  }
  return (
    <PropertyField label={field.label}>
      <NumberField label={`${field.label} value`} value={value * multiplier} min={field.min * multiplier} max={field.max * multiplier} step={field.step * multiplier} decimals={2} suffix={suffix} {...(field.unit === 'length' ? { format: (n: number) => formatLength(n, units, { bare: true }), parse: (text: string) => parseLength(text, units) } : {})} onChange={next => onChange(next / multiplier)} />
      <Slider label={field.label} value={value} min={field.min} max={field.max} step={field.step} onChange={onChange} />
    </PropertyField>
  );
}

type ShaderControl = ShaderDef['controls'][number];
type ControlGroup = { heading: string | null; controls: { control: ShaderControl; label: string }[] };

/** A shader this long gets headings even when its manifest names none. */
const GROUP_FROM = 9;

/**
 * The headings a shader's controls sit under: the ones Lumens gave them (Tape, Tracking,
 * Glitches) when the manifest has them, otherwise for a long shader the first word its
 * labels share, so "Tape noise" and "Tape wow" read as Noise and Wow under Tape. Controls
 * with no heading come first, as General. A shader with one heading or none stays flat.
 */
export function controlGroups(controls: readonly ShaderControl[]): ControlGroup[] {
  const named = (control: ShaderControl) => (control as { group?: string }).group?.trim() || null;
  const headed = controls.some(named);
  const words = new Map<string, number>();
  const firstWord = (label: string) => label.trim().split(/\s+/)[0]!.toLowerCase();
  if (!headed && controls.length >= GROUP_FROM) {
    for (const control of controls) if (/\s/.test(control.label.trim())) words.set(firstWord(control.label), (words.get(firstWord(control.label)) ?? 0) + 1);
  }
  const groups = new Map<string | null, ControlGroup>([[null, { heading: null, controls: [] }]]);
  for (const control of controls) {
    let heading = named(control);
    let label = control.label;
    if (!headed && (words.get(firstWord(label)) ?? 0) >= 2) {
      const [word, ...rest] = label.trim().split(/\s+/);
      heading = word!.charAt(0).toUpperCase() + word!.slice(1);
      label = rest.join(' ');
      label = label.charAt(0).toUpperCase() + label.slice(1);
    }
    const group = groups.get(heading) ?? { heading, controls: [] };
    group.controls.push({ control, label });
    groups.set(heading, group);
  }
  const all = [...groups.values()].filter(group => group.controls.length);
  if (all.length < 2) return [{ heading: null, controls: controls.map(control => ({ control, label: control.label })) }];
  return all.map(group => group.heading === null ? { ...group, heading: 'General' } : group);
}

/** The saved Lumens manifest supplies labels and limits, just as in Lumens. */
function ShaderFields({ effect, definition, onChange }: { effect: ShaderEffect; definition?: ShaderDef | null; onChange: (props: Record<string, unknown>) => void }) {
  const set = (id: string, value: ShaderValue) => onChange({ values: { ...effect.values, [id]: value } });
  const page = useContext(BarFields);
  const controls = definition?.controls ?? [];
  const groups = page ? controlGroups(controls) : [{ heading: null, controls: controls.map(control => ({ control, label: control.label })) }];
  // A long shader opens on its first heading; the rest wait to be asked for.
  const [closed, setClosed] = useState<ReadonlySet<string>>(() => new Set(controls.length > 12 ? groups.slice(1).map(group => group.heading ?? '') : []));
  const field = (control: ShaderControl, label: string) => {
    const value = effect.values[control.id] ?? control.default;
    if (control.type === 'number') return <EffectSlider key={control.id} field={{ key: control.id, label, min: control.min ?? 0, max: control.max ?? 100, step: control.step ?? .01, unit: 'plain' }} value={Number(value) || 0} units="px" onChange={next => set(control.id, next)} />;
    if (control.type === 'select') return <PropertyField key={control.id} label={label}><PopUpButton label={label} value={String(value)} options={(control.options ?? []).map(option => ([String(option.value), option.label] as const))} onChange={next => set(control.id, Number(next))} /></PropertyField>;
    if (control.type === 'boolean') return <PropertyField key={control.id} label={label}><div className="effect-switch"><Switch label={label} checked={Boolean(value)} onChange={next => set(control.id, next)} /></div></PropertyField>;
    if (control.type === 'color' && Array.isArray(value)) {
      const color = { r: value[0] ?? 0, g: value[1] ?? 0, b: value[2] ?? 0, a: value[3] ?? 1 };
      return <EffectColour key={control.id} label={label} hex={colorToHex(color)} alpha={color.a} onChange={hex => { const next = rgba(hex); set(control.id, value.length === 4 ? [next.r, next.g, next.b, color.a] : [next.r, next.g, next.b]); }} />;
    }
    if (control.type === 'vector2' && Array.isArray(value)) return <PropertyField key={control.id} label={label} className="inspector-span"><div className="inspector-grid">{['X', 'Y'].map((axis, index) => <NumberField key={axis} label={`${label} ${axis}`} prefix={axis} value={value[index] ?? 0} min={control.min} max={control.max} step={control.step} onChange={next => set(control.id, index === 0 ? [next, value[1] ?? 0] : [value[0] ?? 0, next])} />)}</div></PropertyField>;
    return null;
  };
  const opacity = <EffectSlider field={{ key: 'opacity', label: 'Opacity', min: 0, max: 1, step: .01, unit: 'percent' }} units="px" value={effect.opacity} onChange={opacity => onChange({ opacity })} />;
  if (groups.length < 2) return <>{groups[0]?.controls.map(({ control, label }) => field(control, label))}{opacity}</>;
  return <>
    {opacity}
    {groups.map(group => {
      const heading = group.heading ?? '';
      const open = !closed.has(heading);
      return <section key={heading} className="effect-group" data-open={open || undefined}>
        <button type="button" className="effect-group-head" aria-expanded={open} onClick={() => setClosed(was => { const next = new Set(was); if (open) next.add(heading); else next.delete(heading); return next; })}>
          <span aria-hidden className="effect-group-chevron"><Icon name="chevron-right" size={12} /></span>
          <span className="effect-group-title">{heading}</span>
          <span className="effect-group-count">{group.controls.length}</span>
        </button>
        {open && <div className="effect-group-body">{group.controls.map(({ control, label }) => field(control, label))}</div>}
      </section>;
    })}
  </>;
}

/**
 * Shader colours use the same square swatch and editable hex value as paints. In a CMYK
 * file a typed hex becomes the press's ink for it, as a picked colour does.
 */
function EffectColour({ label, pickerLabel = label, hex, ink, alpha, onChange }: { label: string; pickerLabel?: string; hex: string; ink?: Cmyk; alpha: number; onChange: (hex: string, ink?: Cmyk) => void }) {
  const press = usePress();
  return <PropertyField label={label}><div className="effect-colour">
    <ColourField label={pickerLabel} title={label} hex={hex} ink={ink} alpha={alpha} onChange={onChange} />
    <HexField label={`${pickerLabel} hex`} hex={hex} onChange={typed => {
      if (!press) return onChange(typed);
      const pressed = pressHex(press, typed);
      onChange(pressed.hex, pressed.ink);
    }} />
  </div></PropertyField>;
}
